import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { usePushServiceSubscription } from '../PushService/PushService';
import _ from 'lodash';
import useApiCall from '../useApiCall';
import useAPI from '../useAPI';


/**
 *
 *
 * @param {*} filterOptions contains filters for the received data, currently supporting assetId as list
 * @param {*} telemetryModels if DTDL content models are included merge values into models as model.value
 * @returns filteredTelemetry or last values of filteredTelemetry inside TelemetryModels
 */
const useTelemetryPushService = ({ filterOptions, telemetryModels }) => {
  const [telemetry, setTelemetry] = useState([]);
  const [hasError, setError] = useState(false);
  const api = useAPI();
  const lastAssetIdFilter = useRef([]);

  const updateTelemetryForAssets = useCallback((results) => {
    // Update telemetry for new assets
    // console.log('results', results);
    setTelemetry(() => {
      // console.log('results', results);
      return _.map(results, result => {
        const formattedLatestTelemetry = {
          assetId: _.head(result)?.entityId,
          telemetry: []
        };

        for (let latestTelemetryItem of result) {
          const arr = _.split(latestTelemetryItem.telemetryName, '.');
          if (_.size(arr) > 1) {
            const item = {
              assetId: _.head(result)?.entityId,
              componentName: arr[0],
              name: arr[1],
              value: latestTelemetryItem.value,
              timestamp: latestTelemetryItem.timestamp
            };
            formattedLatestTelemetry.telemetry.push(item);
          } else {
            const item = {
              assetId: _.head(result)?.entityId,
              componentName: null,
              name: arr[0],
              value: latestTelemetryItem.value,
              timestamp: latestTelemetryItem.timestamp
            };
            formattedLatestTelemetry.telemetry.push(item);
          }
        }
        return formattedLatestTelemetry;
      });
    });
  }, []);

  const [getTelemetries, isLoading] = useApiCall({
    apiCall: api.telemetry.telemetryForAssets,
    onSuccess: updateTelemetryForAssets,
    onError: useCallback(() => setError(true), [])
  });

  // onSuccess: useCallback((results) => {
  //   const data  = _.map(results, result => {
  //     return {
  //       assetId: _.head(result)?.entityId,
  //       telemetry: _.map(result.latestTelemetr)
  //     }
  //   })
  // }, []),

  useEffect(() => {
    // Load new assets if there are any missing
    // This accumulates telemetry

    const assetIdsMissing = [];
    _.forEach(filterOptions.assetIds, assetId => {
      if (!_.some(lastAssetIdFilter.current, ['assetId', assetId])) {
        assetIdsMissing.push(assetId);
      }
    });

    lastAssetIdFilter.current = filterOptions.assetIds;

    if (!_.isEmpty(assetIdsMissing)) {
      getTelemetries(assetIdsMissing);
    }
  }, [filterOptions, getTelemetries]);


  const telemetryPushCallback = useCallback(pushData => {
    // Got push notification for telemetry, update.

    const telemetryMessage = pushData.message;
    const telemetryName = Object.keys(telemetryMessage)[0];
    const newTelemetryValueObject = {
      name: telemetryName,
      componentName: telemetryMessage.componentName,
      value: telemetryMessage[telemetryName],
      timestamp: telemetryMessage.timestamp
    };

    // console.log('telemetryMessage', telemetryMessage);
    setTelemetry(prevTelemetry => {
      const existingAssetIndex = _.findIndex(prevTelemetry, ['assetId', telemetryMessage.entityId]);

      if (existingAssetIndex === -1) {
        const newTelemetryObject = {
          assetId: telemetryMessage.entityId,
          telemetry: [newTelemetryValueObject]
        };
        prevTelemetry.push(newTelemetryObject);
      } else {
        const existingAsset = prevTelemetry[existingAssetIndex];
        const existingTelemetry = _.find(existingAsset.telemetry,
          ({ name, component }) => name === telemetryName && component === newTelemetryValueObject.component);

        if (existingTelemetry) {
          _.assign(existingTelemetry, newTelemetryValueObject);
        } else {
          existingAsset.telemetry.push(newTelemetryValueObject);
        }

        return [...prevTelemetry];
      }
      return prevTelemetry;
    }
    );
  }, []);

  const [, hasErrorInPushService] = usePushServiceSubscription(
    'telemetry',
    telemetryPushCallback,
    filterOptions?.assetIds);

  // Apply filter options
  const filteredTelemetry = useMemo(() => {
    if (filterOptions) {
      const assetIdFilter = filterOptions.assetIds;

      if (!_.isEmpty(assetIdFilter)) {
        const filtered = _.filter(telemetry, telemetry => assetIdFilter.includes(telemetry.assetId));
        const newTelemetryValues = _.head(filtered);
        if (!_.isEmpty(newTelemetryValues) && !_.isEmpty(telemetryModels)) {
          return _.map(telemetryModels, (model) => {
            const value = _.find(newTelemetryValues.telemetry,
              (value) => value.componentName === model.componentName && value.name === model.name);
            return { ...model, value, assetId: newTelemetryValues.assetId };
          });
        }
        return filtered;
      }
    }
    return telemetry;
  }, [filterOptions, telemetry, telemetryModels]);

  return [
    filteredTelemetry,
    isLoading,
    // isLoading || isLoadingPushService,
    hasError || hasErrorInPushService
  ];
};

export { useTelemetryPushService };
